/**
 * What a new note or mind map is CALLED.
 *
 * A deck can hold several of each, and unlike a source — whose number is a
 * pure POSITION, recomputed on every render (see `sourceDisplayLabel`) — a
 * note's number is its NAME. It is written down at creation and never moves
 * again, because the user renames it, refers to it, and generates cards out
 * of it: a name that renumbered itself under them would make "Note 2" mean a
 * different note tomorrow.
 *
 * Two consequences follow from that one decision, and they are the whole
 * module:
 *
 *  - DELETING LEAVES A GAP. Remove "Note 2" of three and the deck reads
 *    "Note 1, Note 3". Renumbering would rewrite a name the user may have
 *    typed into a card, a chat or their own head.
 *  - THE NEXT NUMBER IS ONE PAST THE HIGHEST, never the count. With gaps
 *    allowed, counting collides: [Note 1, Note 3] has a count of two, so
 *    "count + 1" would mint a SECOND "Note 3". One past the highest is the
 *    only rule that is both incrementing and collision-free.
 *
 * Renaming releases a number. Call "Note 2" something else and 2 is free
 * again, because nothing is called it any more — the name is the only thing
 * this reads, so a renamed item simply leaves the numbered population.
 */

/** Titles that are still an auto-number: exactly `<prefix> <digits>`. */
function numberOf(title: string, prefix: string): number | null {
  const trimmed = title.trim();
  if (!trimmed.startsWith(`${prefix} `)) return null;
  const rest = trimmed.slice(prefix.length + 1);
  if (!/^\d+$/.test(rest)) return null;
  const value = Number.parseInt(rest, 10);
  return Number.isSafeInteger(value) && value > 0 ? value : null;
}

/**
 * The name to give the next note / mind map of a deck.
 *
 * `prefix` arrives pre-translated ("Note", "Mind map") — this module carries
 * no i18n, like the rest of the materials layer. A deck whose every item has
 * been renamed starts again at 1, which is right: nothing is called that.
 */
export function nextMaterialName(
  existingTitles: readonly (string | undefined)[],
  prefix: string,
): string {
  let highest = 0;
  for (const title of existingTitles) {
    if (!title) continue;
    const value = numberOf(title, prefix);
    if (value !== null && value > highest) highest = value;
  }
  return `${prefix} ${highest + 1}`;
}
